import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    Alert,
    Box,
    Button,
    Divider,
    Grid,
    Skeleton,
    Stack,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { getApiBaseUrl } from "../../../config/api";
import { getMalawiGrade } from "../../../utils/gradingUtils";
import { allowedReportPeriods, STUDENT_PERIOD_TABS } from "../../../utils/reportPeriodUtils";
import {
    averageFromResultRows,
    resolveStudentResultsByPeriod,
} from "../../../utils/studentResultsByPeriod";
import { ResultsPageContainer, ResponsiveTableWrap } from "./resultsUi";
import PrimaryTermPromotionReview from "../../../components/exam/PrimaryTermPromotionReview";
import { getSchoolIdForUser } from "../../../utils/userContext";

const PERIOD_TABS = STUDENT_PERIOD_TABS;

function PrintSignatureRow({ leftLabel, rightLabel }) {
    return (
        <Grid container spacing={4} sx={{ mt: 4 }}>
            <Grid item xs={12} sm={6}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                    {leftLabel}
                </Typography>
                <Box sx={{ borderBottom: "1px solid #000", height: 36, mb: 0.5 }} />
                <Typography variant="caption" color="text.secondary">
                    Signature &amp; date
                </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                    {rightLabel}
                </Typography>
                <Box sx={{ borderBottom: "1px solid #000", height: 36, mb: 0.5 }} />
                <Typography variant="caption" color="text.secondary">
                    Signature &amp; date
                </Typography>
            </Grid>
        </Grid>
    );
}

function StampBox() {
    return (
        <Box
            sx={{
                width: 100,
                height: 100,
                border: "2px dashed #666",
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                mx: "auto",
                mt: 2,
            }}
        >
            <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
                Official stamp
            </Typography>
        </Box>
    );
}

const StudentReportCard = ({ basePath = "/Admin", readOnly = false }) => {
    const { studentId, id } = useParams();
    const { currentUser } = useSelector((s) => s.user);
    const resolvedId = studentId || id || (readOnly ? currentUser?._id : null);
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [periodKey, setPeriodKey] = useState("endOfTerm");

    const educationLevel = data?.educationLevel || "Secondary";
    const visibleTabs = useMemo(() => {
        const allowed = new Set(["Class test", ...allowedReportPeriods(educationLevel)]);
        return PERIOD_TABS.filter((tab) => allowed.has(tab.reportPeriod));
    }, [educationLevel]);

    const portalOnly = readOnly || String(basePath).startsWith("/Student");

    const resultsByPeriod = useMemo(
        () =>
            resolveStudentResultsByPeriod({
                reportCard: data,
                userDetails: null,
                portalOnly,
            }),
        [data, portalOnly],
    );

    useEffect(() => {
        if (!resultsByPeriod) return;
        const order = visibleTabs.map((tab) => tab.key);
        const firstWithMarks = order.find((key) => (resultsByPeriod[key] || []).length > 0);
        if (firstWithMarks) setPeriodKey(firstWithMarks);
    }, [resultsByPeriod, visibleTabs]);

    const periodResults = resultsByPeriod?.[periodKey] || data?.results || [];
    const periodLabel = visibleTabs.find((tab) => tab.key === periodKey)?.label || "Subject marks";
    const averageMark =
        data?.averageMarkByPeriod?.[periodKey] ??
        averageFromResultRows(resultsByPeriod?.[periodKey]) ??
        data?.averageMark ??
        0;
    const classPosition = periodKey === "midTerm"
        ? data?.classPositionByPeriod?.midTerm ?? data?.classPosition
        : periodKey === "endOfTerm"
          ? data?.classPositionByPeriod?.endOfTerm ?? data?.classPosition
          : null;

    useEffect(() => {
        if (!resolvedId) return;
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const portalQ = readOnly || String(basePath).startsWith("/Student") ? "?portal=true" : "";
                const { data: card } = await axios.get(
                    `${getApiBaseUrl()}/StudentReportCard/${resolvedId}${portalQ}`,
                );
                if (!cancelled) {
                    if (card?.message && !card?.student) {
                        setError(card.message);
                        setData(null);
                    } else {
                        setData(card);
                        setError("");
                    }
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err.response?.data?.message || "Could not load report card");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [resolvedId, readOnly, basePath]);

    const student = data?.student;
    const sclass = student?.sclass;
    const schoolId = getSchoolIdForUser(currentUser);
    const isPrimary = String(educationLevel).trim() === "Primary";
    const academicYear = String(sclass?.academicYear || new Date().getFullYear());
    const schoolName = currentUser?.schoolName || "School";
    const classTeacherName =
        sclass?.classTeacher?.name || sclass?.classTeacherName || "Class teacher";
    const overall = getMalawiGrade(
        averageMark,
        data?.educationLevel || sclass?.educationLevel,
        data?.levelCode || sclass?.levelCode,
        data?.sclassName || sclass?.sclassName
    );
    const attendanceRows = Array.isArray(data?.attendance) ? data.attendance : [];
    const overallAttendance =
        data?.overallAttendancePercentage != null
            ? `${Number(data.overallAttendancePercentage).toFixed(1)}%`
            : "—";

    return (
        <ResultsPageContainer maxWidth="md">
            <Stack direction="row" spacing={1} sx={{ mb: 2 }} className="no-print">
                <Button startIcon={<ArrowBackOutlinedIcon />} onClick={() => navigate(-1)}>
                    Back
                </Button>
                <Box sx={{ flex: 1 }} />
                {!readOnly && data ? (
                    <Button variant="contained" startIcon={<PrintOutlinedIcon />} onClick={() => window.print()}>
                        Print
                    </Button>
                ) : null}
            </Stack>

            {error ? (
                <Alert severity="warning" sx={{ mb: 2 }} className="no-print">
                    {error}
                </Alert>
            ) : null}

            {loading ? (
                <Skeleton variant="rounded" height={480} className="no-print" />
            ) : null}

            {data ? (
                <Box
                    className="print-area"
                    sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        bgcolor: "background.paper",
                        p: { xs: 2, sm: 3 },
                        "@media print": {
                            border: "none",
                            p: 0,
                        },
                    }}
                >
                    <Typography className="print-title" variant="h5" fontWeight={800} align="center" gutterBottom>
                        {schoolName}
                    </Typography>
                    <Typography className="print-subtitle" variant="subtitle1" align="center" color="text.secondary" gutterBottom>
                        Student Report Card — {data.educationLevel || "Secondary"}
                    </Typography>
                    <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 1 }}>
                        Academic year {new Date().getFullYear()} · {periodLabel}
                    </Typography>
                    {resultsByPeriod && visibleTabs.length > 1 ? (
                        <Tabs
                            value={periodKey}
                            onChange={(_, value) => setPeriodKey(value)}
                            variant="scrollable"
                            scrollButtons="auto"
                            className="no-print"
                            sx={{ mb: 2 }}
                        >
                            {visibleTabs.map((tab) => (
                                <Tab key={tab.key} value={tab.key} label={tab.label} />
                            ))}
                        </Tabs>
                    ) : null}
                    <Divider sx={{ my: 2 }} />

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2">
                                <strong>Name:</strong> {student?.name}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Roll No:</strong> {student?.rollNum}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Class teacher:</strong> {classTeacherName}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2">
                                <strong>Class:</strong> {sclass?.sclassName || data?.sclassName || "—"}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Position:</strong>{" "}
                                {classPosition != null ? `${classPosition} of class` : "—"}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Average mark:</strong> {Number(averageMark || 0).toFixed(2)}%
                            </Typography>
                            <Typography variant="body2">
                                <strong>Overall grade:</strong> {overall.grade} ({overall.label})
                            </Typography>
                        </Grid>
                    </Grid>

                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                        {periodLabel}
                    </Typography>
                    <ResponsiveTableWrap>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Subject</TableCell>
                                    {periodKey === "classTests" ? <TableCell>Test</TableCell> : null}
                                    <TableCell align="right">Marks</TableCell>
                                    <TableCell align="center">Grade</TableCell>
                                    <TableCell>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {periodResults.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={periodKey === "classTests" ? 5 : 4} align="center">
                                            No marks recorded yet.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    periodResults.map((row, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell>{row.subjectName}</TableCell>
                                            {periodKey === "classTests" ? (
                                                <TableCell>{row.examLabel || "Class test"}</TableCell>
                                            ) : null}
                                            <TableCell align="right">{row.marksObtained}</TableCell>
                                            <TableCell align="center">{row.grade}</TableCell>
                                            <TableCell>{row.passed ? "Pass" : "Fail"}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </ResponsiveTableWrap>

                    <Box sx={{ mt: 3, mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                            Attendance summary
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Overall attendance:</strong> {overallAttendance}
                        </Typography>
                        {attendanceRows.length > 0 ? (
                            <ResponsiveTableWrap>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Subject</TableCell>
                                            <TableCell align="right">Present</TableCell>
                                            <TableCell align="right">Sessions</TableCell>
                                            <TableCell align="right">%</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {attendanceRows.map((row, idx) => {
                                            const sub =
                                                row.subName?.subName ||
                                                row.subName?.name ||
                                                row.subjectName ||
                                                "—";
                                            const present = row.present ?? row.daysPresent ?? "—";
                                            const sessions = row.sessions ?? row.totalSessions ?? "—";
                                            const pct =
                                                row.percentage != null
                                                    ? `${Number(row.percentage).toFixed(1)}%`
                                                    : sessions && present !== "—" && Number(sessions) > 0
                                                      ? `${((Number(present) / Number(sessions)) * 100).toFixed(1)}%`
                                                      : "—";
                                            return (
                                                <TableRow key={idx}>
                                                    <TableCell>{sub}</TableCell>
                                                    <TableCell align="right">{present}</TableCell>
                                                    <TableCell align="right">{sessions}</TableCell>
                                                    <TableCell align="right">{pct}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </ResponsiveTableWrap>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                Per-subject attendance not recorded.
                            </Typography>
                        )}
                    </Box>

                    {data.teacherRemark ? (
                        <Box sx={{ mt: 2, mb: 2 }}>
                            <Typography variant="subtitle2" fontWeight={700}>
                                Teacher&apos;s remark
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {data.teacherRemark}
                            </Typography>
                        </Box>
                    ) : null}

                    {!readOnly && isPrimary && schoolId && resolvedId ? (
                        <PrimaryTermPromotionReview
                            schoolId={schoolId}
                            studentId={resolvedId}
                            academicYear={academicYear}
                            teacherId={currentUser?._id || currentUser?.id}
                            compact
                        />
                    ) : null}

                    <PrintSignatureRow leftLabel="Class teacher" rightLabel="Principal" />
                    <StampBox />

                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2, textAlign: "center" }}>
                        Generated {new Date().toLocaleDateString()} · MSCE grading scale
                    </Typography>
                </Box>
            ) : null}

            {!readOnly && resolvedId ? (
                <Stack direction="row" spacing={1} sx={{ mt: 2 }} className="no-print">
                    <Button variant="outlined" onClick={() => navigate(`${basePath}/students/student/marks/${resolvedId}`)}>
                        Edit marks
                    </Button>
                </Stack>
            ) : null}
        </ResultsPageContainer>
    );
};

export default StudentReportCard;
